import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { View } from 'react-native';
import { createContainer, defer } from '@dbk-legacy/muster/react';
import capitalize from 'lodash/capitalize';
import { ref } from '@dbk-legacy/muster/utils';

import { MARKER_COLOURS } from '../../../constants';

import Error from '../../../components/common/error';
import { LoadingCircle } from '../../../components/common/loading';
import { Large } from '../../../components/common/text';
import AbbreviatedNotes from '../../../components/common/abbreviated-notes';

import PositionHeader from './position-header/position-header-container';
import Transaction from './transaction/transaction-container';
import TransactionDetails from './transaction-details/transaction-details-container';
import ScrollableLayout from '../../../components/common/scrollable-layout';

import SortingHeader from '../../../components/common/sorting-header/sorting-header-container';
import ExpandableListView from '../../../components/common/expandable-list-view/expandable-list-view-container';
import { StickyContainer, Sticky } from 'react-sticky';
import styles from './position-transactions-factory.styles';

class Header extends PureComponent {
  render() {
    const { uiStatic, uiState, currency, screen, messages} = this.props;

    return (
      <div style={{ ...this.props.style, overflow: 'auto'}}>
          <SortingHeader
            uiStatic={uiStatic}
            state={uiState}
            currency={currency}
            screen={screen}
            messages={messages}
          />
      </div>
    );
  }
}

export default function assetPositionsFactory({
  assetCategory,
  HeaderComponent = PositionHeader,
  SortingHeaderComponent = SortingHeader,
  RowComponent = Transaction,
  ExpandedRowComponent = TransactionDetails,
}) {
  function AssetPositions({ ui, portfolio, screen, i18n: {messages}}) {
    let canExpand;
    if(!screen.isSmall && (assetCategory === 'equities' ||  assetCategory == 'bonds' || assetCategory == 'various' ||  assetCategory == 'optionsDerivatives' || assetCategory == 'alternatives' || assetCategory == 'cash')){
      canExpand = false;
    } else {
      canExpand = true;
    }

    const uiStatic = ui.static[assetCategory].position;
    const positionData = ui.positions[assetCategory].currentPosition;
    const uiState = ui.state.position;

    const rows = positionData.transactions.filter(row => row);
    rows.map((row) => {
      row.canExpand = canExpand;
    })

    const emptyView = (
      <View style={styles.root} accessible={true} accessibilityLabel={messages['app.position.noTransDetails']}>
        <Large type="secondary" style={styles.emptyText}>{messages['app.position.noTransDetails']}</Large>
      </View>
    );

    const listView = (
      <ExpandableListView
        uiStatic={uiStatic}
        state={uiState}
        messages={messages}
        rows={rows}
        rowMarkerStyle={MARKER_COLOURS[assetCategory]}
        renderRow={row => <RowComponent
          row={row}
          uiStatic={uiStatic}
          messages={messages}
          assetCategory={assetCategory}
          screen={screen}
          abbreviationRules={ui.static.abbreviationRules}
        />}
        renderRowExpanded={(row) => <ExpandedRowComponent
          uiStatic={uiStatic}
          row={row}
          assetCategory={assetCategory}
          abbreviationRules={ui.static.abbreviationRules}
          messages={messages}
        />}
        footer={<AbbreviatedNotes />}
      />
    );

    const bodyView = rows.length === 0 ? emptyView : listView;

    return (
      <div className={'position-transactions row'}>
        <StickyContainer style={{minHeight: screen.height/2.4 ,marginTop: '5px'}}>
          <HeaderComponent 
            uiStatic={uiStatic} 
            positionData={positionData} 
            currency={positionData.currency} 
            portfolio={portfolio} 
            messages={messages}/>
          <Sticky fixedTop={90}>
                {
                  ({ isSticky, wasSticky, style, distanceFromTop, distanceFromBottom, calculatedHeight }) => {
                    return <Header 
                            style={style} 
                            uiStatic={uiStatic} 
                            uiState={uiState} 
                            currency={positionData.currency} 
                            screen={screen} 
                            messages={messages}/>
                  }
                }
          </Sticky>
          <ScrollableLayout
            testID={`${assetCategory}-view`}
            body={bodyView}
          />
          </StickyContainer>
      </div>
    );
  }

  AssetPositions.propTypes = {
    app: PropTypes.object,
    portfolio: PropTypes.shape({
      [assetCategory]: PropTypes.object,
    }),
    ui: PropTypes.object,
  };

  AssetPositions.displayName = `${capitalize(assetCategory)}AssetPositions`;

  return createContainer({
    $renderError: () => <Error />,
    $renderLoading: () => <LoadingCircle isPage={true}/>,
    $queries: {
      settings: {
        currentEnvironment: {
          id: true,
        },
      },
      screen:{
        height: true,
        width: true,
        isSmall: true,
      },
      i18n: {
        messages: true,
      },
      ui: {
        static: {
          [assetCategory]: {
            position: {
              ...ExpandableListView.getFragment('uiStatic'),
              ...RowComponent.getFragment('uiStatic'),
              ...SortingHeader.getFragment('uiStatic'),
              ...HeaderComponent.getFragment('uiStatic'),
              ...ExpandedRowComponent.getFragment('uiStatic'),
            },
          },
          abbreviationRules: true,
        },

        state: {
          position: {
            ...ExpandableListView.getFragment('state'),
            ...SortingHeader.getFragment('state'),
          },
        },

        positions: {
          [assetCategory]: {
            currentPosition: {
              ...HeaderComponent.getFragment('positionData'),

              currency: true,
              transactions: {
                $type: 'list',
                $range: {
                  offset: 0,
                  length: ref(`ui.positions.currentPosition.transactions.length`)
                 // length: ref(`ui.positions.${assetCategory}.currentPosition.transactions.length`),
                },
                $node: defer({
                  type: true, // TODO: remove when DGUI-948 is fixed

                  ...RowComponent.getFragment('row'),
                  ...ExpandedRowComponent.getFragment('row'),
                }),
              },
            },
          },
        },
      },
      portfolio: {
          ...HeaderComponent.getFragment('portfolio'),
      },
    },
  })(AssetPositions);
}
